import { createMemo, type Accessor } from 'solid-js';
import { useApiServices } from '@trimble/tc-issue-management-providers';
import type { CheckFollowStatusBulkData } from '@data-access/connect/tc-follow';

/**
 * Custom hook that manages all project-related API queries
 */
export type ProjectQueries = {
  userQuery: ReturnType<
    ReturnType<typeof useApiServices>['userApi']['getUserMeQuery']
  >;
  accessPolicyQuery: ReturnType<
    ReturnType<typeof useApiServices>['ecomApi']['getAccessPolicyQuery']
  >;
  projectUsersQuery: ReturnType<
    ReturnType<typeof useApiServices>['tcpsProjectApi']['getProjectUsersQuery']
  >;
  projectGroupsQuery: ReturnType<
    ReturnType<typeof useApiServices>['tcpsGroupApi']['getProjectGroupsQuery']
  >;
  configurationQuery: ReturnType<
    ReturnType<typeof useApiServices>['issueApi']['getConfigurationQuery']
  >;
  issuesQuery: Accessor<ReturnType<
    ReturnType<typeof useApiServices>['issueApi']['listAllIssuesPaginatedQuery']
  > | null>;
  tagsQuery: ReturnType<
    ReturnType<typeof useApiServices>['tagApi']['getTagsQuery']
  >;
  followStatusQuery: Accessor<ReturnType<
    ReturnType<typeof useApiServices>['followApi']['checkFollowStatusQuery']
  > | null>;
  listIconsQuery: Accessor<ReturnType<
    ReturnType<typeof useApiServices>['issueApi']['listIconsQuery']
  > | null>;
  getDefaultIconsQuery: ReturnType<
    ReturnType<typeof useApiServices>['issueApi']['getDefaultIconsQuery']
  >;
};

export function useProjectQueries(
  projectId: string,
  contextId: string | undefined
): ProjectQueries {
  const apiServices = useApiServices();

  // User and authentication queries
  const userQuery = apiServices.userApi.getUserMeQuery();
  const accessPolicyQuery = apiServices.ecomApi.getAccessPolicyQuery();

  // Project-specific queries
  const projectUsersQuery = apiServices.tcpsProjectApi.getProjectUsersQuery(
    () => ({
      path: { projectId },
    })
  );

  const projectGroupsQuery = apiServices.tcpsGroupApi.getProjectGroupsQuery(
    () => ({
      query: { projectId },
    })
  );

  // Configuration query that depends on contextId
  const configurationQuery = apiServices.issueApi.getConfigurationQuery(
    () => contextId
  );

  // Issues query that only runs when contextId is available
  const issuesQuery = createMemo(() => {
    if (!contextId) {
      return null; // Don't create query until we have a contextId
    }

    return apiServices.issueApi.listAllIssuesPaginatedQuery(() => contextId, {
      query: {
        fields: [
          'status',
          'type',
          '_actions',
          'assignees',
          'priority',
          'dueDate',
          'description',
          'marker',
        ],
      },
    });
  });

  // Tags query
  const tagsQuery = apiServices.tagApi.getTagsQuery(() => ({
    query: {
      projectId,
    },
  }));

  // Follow status query - loads follow statuses for all issues in bulk
  const followStatusQuery = createMemo(() => {
    if (!contextId || !issuesQuery()?.data) {
      return null;
    }

    // Get current user ID for follow status checks
    const currentUserId = userQuery.data?.tiduuid;
    if (!currentUserId) {
      return null;
    }

    // Prepare bulk follow status check data for all issues
    const issues = issuesQuery()?.data?.items || [];
    const followCheckData: CheckFollowStatusBulkData['body'] = {
      resources: issues.map((issue) => ({
        followerId: `users:${currentUserId}`,
        resourceId: `issues:${issue.id}`,
      })),
    };
    return apiServices.followApi.checkFollowStatusQuery(
      () => contextId,
      () => followCheckData
    );
  });

  // Icon queries
  const listIconsQuery = createMemo(() => {
    if (!contextId) {
      return null;
    }
    return apiServices.issueApi.listIconsQuery(() => contextId);
  });

  const getDefaultIconsQuery = apiServices.issueApi.getDefaultIconsQuery();

  return {
    userQuery,
    accessPolicyQuery,
    projectUsersQuery,
    projectGroupsQuery,
    configurationQuery,
    issuesQuery,
    tagsQuery,
    followStatusQuery,
    listIconsQuery,
    getDefaultIconsQuery,
  };
}
